<template>
  <banner v-if="!isAgentChatEnabled" />
  <template v-else>
    <div v-if="isHomeShown" class="text-center px-1 home">
      <div
        class="text-th-secondary home-greet"
        data-testid="ai-chat-home-greeting"
      >
        {{ t('aichat.main.home.greeting') }}
      </div>
      <div class="home-sub">
        {{ t('aichat.main.home.description') }}
      </div>
    </div>
    <notice-block v-if="gateNotice" :block="gateNotice" class="gate-notice" />
    <conversation
      v-if="isConversationShown"
      :turns="chat.turns"
      @followup="ask"
      @resend="resend"
    />
    <composer
      v-show="!isPromptListOpen"
      :credits-left="creditsLeft"
      :credits-limit="creditsLimit"
      :is-streaming="chat.isStreaming"
      :disabled="!canAsk"
      @send="ask"
      @stop="chat.stop()"
      @browse="openPromptList"
    />
    <prompt-top
      v-if="isHomeShown"
      :disabled="!canAsk"
      @view-all="openPromptList"
      @ask="ask"
    />
    <prompt-list
      v-if="isPromptListOpen"
      :disabled="!canAsk"
      @close="closePromptList"
      @ask="askFromList"
    />
    <limit v-if="isLimitShown" />
  </template>
</template>

<script setup lang="ts">
import { computed, inject, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import Banner from './components/controls/banner.vue'
import Composer from './components/composer.vue'
import Conversation from './components/conversation.vue'
import Limit from './components/controls/limit.vue'
import NoticeBlock from './components/blocks/notice.vue'
import PromptTop from './components/controls/prompt-top.vue'
import PromptList from './components/controls/prompt-list.vue'
import { useAgentChatStore } from '@shared/store/agent-chat'
import type { NoticeBlock as NoticeBlockData, Turn } from './types'

const props = withDefaults(
  defineProps<{
    /* null means the merchant is not on a limited plan, so the credits popover stays disabled */
    creditsLeft?: number | null
    creditsLimit?: number | null
  }>(),
  { creditsLeft: null, creditsLimit: null }
)

const chat = useAgentChatStore()
const { t, locale } = useI18n()
const isFeatureEnabled = inject<(flag: string) => boolean>(
  'isFeatureEnabled',
  () => false
)

const isPromptListOpen = ref(false)

const isAgentChatEnabled = computed(() => isFeatureEnabled('agent_chat'))
const isConversationShown = computed(
  () => !isPromptListOpen.value && chat.turns.length > 0
)
/* the greeting and the top prompts only belong to an empty chat */
const isHomeShown = computed(
  () => !isPromptListOpen.value && chat.turns.length === 0
)
/* null is an unlimited plan, so only a number below 1 means the credits are used up */
const isLimitShown = computed(
  () => props.creditsLeft !== null && props.creditsLeft < 1
)
/* the composer and the suggested prompts share this gate: feature active and credits left */
const canAsk = computed(() => !chat.isUpsell && !isLimitShown.value)

/* the account cannot chat at all: the feature is inactive, or the catalogue could not be read before any turn */
const gateNotice = computed<NoticeBlockData | null>(() => {
  if (isPromptListOpen.value) return null
  if (chat.isUpsell) {
    return {
      id: 'upsell',
      type: 'notice',
      level: 'info',
      code: 'FEATURE_INACTIVE',
      title: t('aichat.main.errors.upsell_title'),
      text: t('aichat.main.errors.upsell_text')
    }
  }
  if (chat.gateError && !chat.hasTurns) {
    return {
      id: 'gate-error',
      type: 'notice',
      level: 'error',
      code: chat.gateError,
      title: t('aichat.main.errors.generic'),
      text: t(`aichat.main.errors.${chat.gateError}`)
    }
  }
  return null
})

const openPromptList = (): void => {
  isPromptListOpen.value = true
}

const closePromptList = (): void => {
  isPromptListOpen.value = false
}

const ask = (prompt: string): void => {
  if (!canAsk.value) return
  chat.send(prompt, { locale: locale.value })
}

const askFromList = (prompt: string): void => {
  closePromptList()
  ask(prompt)
}

const resend = (turn: Turn): void => {
  if (!canAsk.value) return
  chat.resend(turn, { locale: locale.value })
}

onMounted(() => {
  if (isAgentChatEnabled.value) chat.loadCatalogue()
})
</script>

<style scoped>
.home {
  display: flex;
  flex-direction: column;
  justify-content: end;
  height: 30%;
}

.home-greet {
  margin: 4px 0;
  font-size: 24px;
  font-weight: 500;
}

.home-sub {
  margin: 0 0 16px;
  color: #6b7686;
  font-size: 11.5px;
}

.gate-notice {
  max-width: none;
  margin-bottom: 16px;
}
</style>
